import { useId } from "react";
import styles from "./m3.module.css";

/**
 * Outlined text/number field with label, leading icon, unit suffix, helper and error text.
 *
 * @param {"text"|"number"|"url"|"date"} type
 * @param {string} suffix - unit shown inside the field, e.g. "kg" or "tCO₂e"
 * @param {React.ReactNode} leading - icon inside the field on the left
 * @param {string} helper - help text under the field
 * @param {string} error - error text; turns the field red and replaces the helper
 * @param {2|"all"} span - widen inside a FieldGrid
 * @param {boolean} large - bigger, bolder input for key values
 * @param {function} onChange - receives the raw string value
 */
const M3Field = ({
  label,
  type = "text",
  value,
  onChange,
  placeholder,
  suffix,
  leading,
  helper,
  error,
  required = false,
  disabled = false,
  span,
  large = false,
  min,
  max,
  step,
  inputProps = {},
  className = "",
}) => {
  const id = useId();
  const spanClass = span === 2 ? styles.span2 : span === "all" ? styles.spanAll : "";
  const boxClass = [
    styles.fieldBox,
    large ? styles.fieldBoxLg : "",
    error ? styles.fieldBoxError : "",
    disabled ? styles.fieldBoxDisabled : "",
  ].join(" ");

  return (
    <div className={`${styles.field} ${spanClass} ${className}`}>
      {label && (
        <label htmlFor={id} className={styles.fieldLabel}>
          {label}
          {required && <span className={styles.fieldRequired} aria-hidden>*</span>}
        </label>
      )}
      <div className={boxClass}>
        {leading && <span className={styles.fieldAdorn}>{leading}</span>}
        <input
          id={id}
          type={type}
          className={styles.fieldInput}
          value={value ?? ""}
          onChange={(e) => onChange && onChange(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          min={min}
          max={max}
          step={step}
          inputMode={type === "number" ? "decimal" : undefined}
          aria-invalid={Boolean(error)}
          aria-describedby={error || helper ? `${id}-help` : undefined}
          {...inputProps}
        />
        {suffix && <span className={styles.fieldAdorn}>{suffix}</span>}
      </div>
      {(error || helper) && (
        <span id={`${id}-help`} className={`${styles.fieldHelper} ${error ? styles.fieldHelperError : ""}`}>
          {error || helper}
        </span>
      )}
    </div>
  );
};

export default M3Field;
